/* Tarot Timing V2 engine — faithful runtime for the design markup.
   Live adaptations: theme header/footer replace the design's own, the
   10-card demo deck grows to the full canonical 78 (the ten hand-written
   timing cards keep their design text; the rest compose from the suit-tempo
   and rank-phase grid the page also documents), the timeline lens note
   upgrades to an audited DeepSeek insight from /api/tarot-timing, the
   price shimmer binds to the REAL rail isPriceReady signal instead of the
   design's 900ms demo timer, and checkout runs on the proven shared rail
   (type "Tarot Timing", shared_tool intents, three-position signals). */
(function bootTiming() {
  'use strict';
  if (document.readyState === 'loading') {
    document.addEventListener('readystatechange', function ready() {
      if (document.readyState === 'loading') return;
      document.removeEventListener('readystatechange', ready);
      bootTiming();
    });
    return;
  }
  if (!document.querySelector('[data-tt2]')) return;
  if (document.querySelector('[data-tt2]').hasAttribute('data-tt2-ready')) return;
  document.querySelector('[data-tt2]').setAttribute('data-tt2-ready', 'true');

  var PAGE = '/pages/tarot-timing';
  var API_BASE = 'https://reading.deckaura.com/api/tarot-timing';
  var KEY = 'deckaura.tarottiming.v2';
  var RAIL_TIERS = { delay: 'deeper', map: 'indepth' };
  var dictionary = {};
  try { dictionary = JSON.parse((document.querySelector('[data-tt2-i18n]') || {}).textContent || '{}'); } catch (_) {}
  Object.keys(dictionary).forEach(function (key) {
    if (typeof dictionary[key] !== 'string') return;
    var decoder = document.createElement('textarea');
    decoder.innerHTML = dictionary[key].replace(/</g, '&lt;').replace(/>/g, '&gt;');
    dictionary[key] = decoder.value;
  });
  function t(key, values) {
    var text = typeof dictionary[key] === 'string' && !/^translation missing:/i.test(dictionary[key]) ? dictionary[key] : key;
    return text.replace(/\{([a-zA-Z0-9_]+)\}/g, function (match, name) { return values && values[name] != null ? String(values[name]) : match; });
  }
  var translate = t;
  function pageLocale() { return (document.documentElement.lang || 'en').toLowerCase(); }

  /* ------------ curated timing cards (design verbatim) ------------ */
  var CURATED = {
    'Eight of Wands': { g: 'VIII', up: { p: 2, s: 'Movement is already in transit.', b: 'Replies, decisions and news travel quickly under this card. Less is stuck than it feels; what is missing is the confirmation, not the momentum.' }, rev: { p: 0, s: 'Something in motion has been held back.', b: 'The intention exists but the delivery is delayed, like a message drafted and not sent, or a decision made and not shared. Pace returns when the held piece is released.' } },
    'The Chariot': { g: 'VII', up: { p: 2, s: 'One party is driving this forward deliberately.', b: 'Progress here depends on direction rather than luck. When someone commits to a single course, the timeline shortens sharply.' }, rev: { p: 0, s: 'Effort is being spent without direction.', b: 'Pushing harder is currently producing less. The situation moves again once the approach changes, not when the pressure increases.' } },
    'Two of Swords': { g: 'II', up: { p: 0, s: 'A decision is being deliberately suspended.', b: 'Nothing advances while the choice stays balanced. The timing follows the decision, so the window depends on when the avoidance ends.' }, rev: { p: 1, s: 'A stalemate is beginning to break.', b: 'Information that was missing is arriving, which makes the deadlock unstable. Expect a position to be stated sooner than the last few weeks suggested.' } },
    'The Hanged Man': { g: 'XII', up: { p: 0, s: 'This is a pause with a purpose, not a dead end.', b: 'The delay is doing work: a perspective is changing before anything visible moves. Acting early here usually resets the clock.' }, rev: { p: 1, s: 'The waiting period has outlived its usefulness.', b: 'What was a considered pause has become simple stalling. Movement now depends on someone choosing to end it.' } },
    'Wheel of Fortune': { g: 'X', up: { p: 2, s: 'A turn is already underway outside your control.', b: 'Circumstances rather than effort set this pace. Timing tends to arrive as a shift in conditions, such as a change of role, place or availability.' }, rev: { p: 0, s: 'The same cycle is repeating and resetting the timing.', b: 'A familiar pattern is running again, which pushes the window later. Breaking the repetition matters more than waiting through it.' } },
    'Four of Swords': { g: 'IV', up: { p: 0, s: 'A necessary rest period comes before movement.', b: 'Recovery is the current task, and it is not wasted time. The window opens after the pause completes rather than during it.' }, rev: { p: 1, s: 'Restlessness is ending the quiet phase.', b: 'Patience is running out on at least one side. That impatience is what produces the first visible step.' } },
    'Page of Cups': { g: 'P', up: { p: 2, s: 'An unplanned opening or message is close.', b: 'The first move tends to be small, informal and emotionally driven. Read it as a beginning, not as a resolution.' }, rev: { p: 0, s: 'An overture may arrive without substance behind it.', b: 'Contact is possible, but it may not carry a real intention. Watch what follows the first message rather than the message itself.' } },
    'Seven of Pentacles': { g: 'VII', up: { p: 0, s: 'Results are growing but are not yet due.', b: 'Work already done is still maturing under the surface. Longer horizons read more accurately here than short ones.' }, rev: { p: 1, s: 'Impatience is being mistaken for a delay.', b: 'The process is running roughly to schedule; the expectation was faster than reality. Adjusting the horizon changes the answer more than pushing does.' } },
    'The Star': { g: 'XVII', up: { p: 1, s: 'Repair is happening quietly and gradually.', b: 'Progress here is slow, calm and real. It shows up as steadiness returning rather than as a single visible event.' } , rev: { p: 0, s: 'Hope is carrying the situation instead of action.', b: 'Expectation is currently doing the work a decision should do. Nothing accelerates until something concrete is asked for.' } },
    'King of Swords': { g: 'K', up: { p: 1, s: 'A verdict rests with one clear decision-maker.', b: 'The timing belongs to whoever holds the authority here. Once they rule, the process moves quickly and formally.' }, rev: { p: 0, s: 'No one is holding the decision.', b: 'Responsibility is unclear, so the timeline drifts. Identifying who actually decides is the fastest available accelerator.' } }
  };

  /* ------------ 78-card engine (suit tempo x rank phase) ------------ */
  var MAJOR_ORDER = ['The Fool', 'The Magician', 'The High Priestess', 'The Empress', 'The Emperor', 'The Hierophant', 'The Lovers', 'The Chariot', 'Strength', 'The Hermit', 'Wheel of Fortune', 'Justice', 'The Hanged Man', 'Death', 'Temperance', 'The Devil', 'The Tower', 'The Star', 'The Moon', 'The Sun', 'Judgement', 'The World'];
  var ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI'];
  var MAJOR_META = {
    'The Fool': { kw: 'an unplanned opening', pUp: 2, pRev: 0 },
    'The Magician': { kw: 'a deliberate first move with the tools already in hand', pUp: 2, pRev: 0 },
    'The High Priestess': { kw: 'information that has not surfaced yet', pUp: 0, pRev: 1 },
    'The Empress': { kw: 'slow, organic growth that resists forcing', pUp: 0, pRev: 0 },
    'The Emperor': { kw: 'structure, rules and someone setting terms', pUp: 1, pRev: 0 },
    'The Hierophant': { kw: 'a formal or traditional process with its own calendar', pUp: 0, pRev: 1 },
    'The Lovers': { kw: 'a values choice that must be made first', pUp: 1, pRev: 0 },
    'Strength': { kw: 'steady, patient handling rather than force', pUp: 1, pRev: 0 },
    'The Hermit': { kw: 'a withdrawal to think that precedes any visible step', pUp: 0, pRev: 1 },
    'Justice': { kw: 'a ruling, a fair weighing, or paperwork completing', pUp: 1, pRev: 0 },
    'Death': { kw: 'an ending that has to clear space first', pUp: 1, pRev: 0 },
    'Temperance': { kw: 'gradual blending and calibrated middle steps', pUp: 1, pRev: 0 },
    'The Devil': { kw: 'a binding habit or arrangement holding the clock', pUp: 0, pRev: 1 },
    'The Tower': { kw: 'a sudden restructuring that resets the board', pUp: 2, pRev: 1 },
    'The Moon': { kw: 'mixed signals and unclear ground', pUp: 0, pRev: 1 },
    'The Sun': { kw: 'clear, visible success conditions', pUp: 2, pRev: 1 },
    'Judgement': { kw: 'a decisive call that closes an old chapter', pUp: 2, pRev: 0 },
    'The World': { kw: 'a full completion arriving in its own final stage', pUp: 1, pRev: 0 }
  };
  var SUIT_META = {
    Wands: { pUp: 2, pRev: 0, tempo: 'in quick bursts, measured in days and weeks', drag: 'the held-back energy being released' },
    Cups: { pUp: 1, pRev: 0, tempo: 'at a feeling-led pace that builds across weeks', drag: 'the feeling being said out loud instead of carried' },
    Swords: { pUp: 1, pRev: 0, tempo: 'through decisions, jumping forward once a choice lands', drag: 'the postponed decision finally being made' },
    Pentacles: { pUp: 1, pRev: 0, tempo: 'slowly and structurally, the way practical processes move across months', drag: 'one practical requirement being completed' }
  };
  var RANK_META = {
    Ace: { g: 'A', dp: 1, phase: 'a clean opening', up: 'A genuine green light sits at the start of this.', rev: 'An opening exists but has not been taken yet.' },
    Two: { g: 'II', dp: 0, phase: 'a balance point', up: 'Two elements are being weighed before anything moves.', rev: 'The weighing has stalled into avoidance.' },
    Three: { g: 'III', dp: 0, phase: 'first visible growth', up: 'The first visible results are close to the surface.', rev: 'Early progress is running slower than planned.' },
    Four: { g: 'IV', dp: 0, phase: 'consolidation', up: 'Things are being made stable before the next step.', rev: 'Holding on is what is currently slowing the clock.' },
    Five: { g: 'V', dp: 0, phase: 'friction', up: 'A point of friction has to be crossed before the pace improves.', rev: 'The friction is easing and the road is clearing.' },
    Six: { g: 'VI', dp: 1, phase: 'movement after difficulty', up: 'Transition is underway toward calmer conditions.', rev: 'Leaving the old situation is proving harder than expected.' },
    Seven: { g: 'VII', dp: 0, phase: 'assessment', up: 'A review is in progress before anything is committed.', rev: 'The review has drifted into second-guessing.' },
    Eight: { g: 'VIII', dp: 1, phase: 'acceleration', up: 'The pace itself is the message here.', rev: 'Speed is being lost to scattered effort.' },
    Nine: { g: 'IX', dp: 0, phase: 'near completion', up: 'This is closer to done than it looks from inside.', rev: 'The last stretch is heavier than the middle was.' },
    Ten: { g: 'X', dp: 0, phase: 'completion and handover', up: 'A full cycle is closing, which is what opens the next one.', rev: 'An ending is being resisted, which delays the restart.' },
    Page: { g: 'P', dp: 1, phase: 'a message or small beginning', up: 'The first move tends to be small and informal.', rev: 'A signal may arrive without much substance behind it.' },
    Knight: { g: 'N', dp: 1, phase: 'active pursuit', up: 'Someone is actively pushing this forward.', rev: 'The push is uneven, fast then absent.' },
    Queen: { g: 'Q', dp: 0, phase: 'steady tending', up: 'Consistent tending, not a single event, sets this pace.', rev: 'Care has tipped into managing instead of moving.' },
    King: { g: 'K', dp: 0, phase: 'a ruling decision', up: 'The timing belongs to whoever holds authority here.', rev: 'No one is clearly holding the decision.' }
  };
  var RANKS = ['Ace', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Page', 'Knight', 'Queen', 'King'];
  var SUITS = ['Wands', 'Cups', 'Swords', 'Pentacles'];

  function clampP(v) { return Math.max(0, Math.min(2, v)); }
  function composedMinor(rank, suit) {
    var r = RANK_META[rank], su = SUIT_META[suit];
    return {
      g: r.g,
      up: { p: clampP(su.pUp + (r.dp || 0) - (rank === 'Queen' || rank === 'King' ? 1 : 0)),
        s: r.up,
        b: 'In timing terms this is ' + r.phase + ' meeting a suit that moves ' + su.tempo + '. The window responds to that rhythm rather than to pressure, so match your expectations to it.' },
      rev: { p: clampP(su.pRev),
        s: r.rev,
        b: 'Reversed, the ' + r.phase + ' is present but not yet delivering. Movement tends to return with ' + su.drag + ', which usually matters more here than more waiting.' }
    };
  }
  function composedMajor(name) {
    var m = MAJOR_META[name];
    return {
      g: ROMAN[MAJOR_ORDER.indexOf(name)],
      up: { p: m.pUp, s: 'The clock here is set by ' + m.kw + '.',
        b: 'Major Arcana cards mark structural timing: the situation moves when ' + m.kw + ' actually happens, not on a steady schedule. Watch the condition itself rather than the calendar.' },
      rev: { p: m.pRev, s: 'The structural shift is present but incomplete.',
        b: 'Reversed, ' + m.kw + ' is being resisted or only half-made, which stretches the window. The pace recovers once that shift is allowed to finish.' }
    };
  }
  var DECK = [];
  MAJOR_ORDER.forEach(function (name) { DECK.push({ n: name, d: CURATED[name] || composedMajor(name) }); });
  SUITS.forEach(function (suit) {
    RANKS.forEach(function (rank) {
      var name = rank + ' of ' + suit;
      DECK.push({ n: name, d: CURATED[name] || composedMinor(rank, suit) });
    });
  });

  /* ------------ design data ------------ */
  var FOCI = [
    { k: 'contact', label: 'Contact', ph: 'When could communication realistically begin again?' },
    { k: 'reconciliation', label: 'Reconciliation', ph: 'What would need to change before a genuine repair is possible?' },
    { k: 'newlove', label: 'New Love', ph: 'What could influence when a meaningful new connection enters my life?' },
    { k: 'career', label: 'Career', ph: 'What is affecting the timing of a job offer or decision?' },
    { k: 'decision', label: 'Decision', ph: 'When will I have enough clarity to decide?' },
    { k: 'other', label: 'Other', ph: 'What are you waiting to see happen, and by when?' }
  ];
  var HORIZONS = [
    { k: '14d', label: '7–14 days', long: '7–14 days', apiLong: '7 to 14 days', end: 'Day 14', plan: 'Short-Window Action Plan' },
    { k: '30d', label: '30 days', long: '30 days', apiLong: '30 days', end: 'Day 30', plan: 'Weekly Timing Roadmap' },
    { k: '3m', label: '3 months', long: '3 months', apiLong: '3 months', end: 'Month 3', plan: 'Phase-Based Timeline' },
    { k: '6m', label: '6 months', long: '6 months', apiLong: '6 months', end: 'Month 6', plan: 'Milestone & Reassessment Map' },
    { k: '12m', label: '12 months', long: '12 months', apiLong: '12 months', end: 'Month 12', plan: 'Milestone & Reassessment Map' }
  ];
  var ROLES = [
    { no: '01', role: 'Current Momentum', hint: 'This position describes how the situation is moving right now.' },
    { no: '02', role: 'Timing Signal', hint: 'This position shows where the pace currently sits inside your horizon.' },
    { no: '03', role: 'Pace Changer', hint: 'This position names what may accelerate or delay the outcome.' }
  ];
  var DELAY_BULLETS = {
    contact: ['Likely contact window', 'What is delaying communication', 'Signs that outreach is becoming more likely', 'Whether initiating or waiting is more grounded', 'Wait, act or reassess guidance'],
    reconciliation: ['Contact window vs reconciliation window', 'What must happen before genuine repair', 'False return vs sincere return signs', 'Your reassessment point', 'Wait, act or reassess guidance'],
    newlove: ['Readiness window and likely meeting phase', 'What is holding the opening back', 'Where movement is most likely to begin', 'What could increase exposure to the opportunity', 'Wait, act or reassess guidance'],
    career: ['Decision window', 'Process bottleneck', 'Next observable hiring signal', 'Best action before the window closes', 'Wait, act or reassess guidance'],
    decision: ['Primary and alternate window', 'Main dependency', 'Three signs the decision is ripening', 'The milestone required first', 'Wait, act or reassess guidance'],
    other: ['Primary and alternate window', 'Main blocker', 'Strongest accelerator', 'Three observable signs to watch', 'Wait, act or reassess guidance']
  };
  var PKGS = [
    { k: 'delay', name: 'Timing & next step', short: 'Timing & next step', price: '', cta: 'Select this reading', words: '750–950 words',
      tagline: 'Understand what is slowing the situation down and what could create real movement.' },
    { k: 'map', name: 'Complete timing plan', short: 'Complete timing plan', price: '', cta: 'Select this reading', words: '1,250–1,500 words',
      tagline: 'See how the timing changes across the current path, an intentional-action path and a delay path.' }
  ];

  /* ------------ state ------------ */
  var s = {
    focus: 'other', horizon: '30d', q: '', ctx: '', ctxOpen: false, consent: false,
    errs: {}, tried: false, phase: 'form', busy: false,
    drawn: null, open: {}, mCard: 0,
    eventDef: '', plan: 'delay', planTouched: false,
    priceReady: false, priceErr: false, pricing: null, checkoutBusy: false, cmp: false,
    server: null, fetching: false, insightFailed: false, insightLimited: false, insightLimit: null,
    readingId: '', live: '', restoreNotice: false
  };
  var t1 = 0, pricePoll = 0, priceWaitMs = 0, railReadingId = '', insightController = null, insightRetryTimer = 0;
  var runtimeLoad = null, runtimeFailed = false, runtimeAttempt = 0;
  var stickyObserver = null, stickyObservedTarget = null, stickyMainInView = false, stickyScrollBound = false;
  var checkoutRun = 0, checkoutOperation = null, cartReviewKey = '';

  /* ------------ helpers ------------ */
  function $(sel) { return document.querySelector(sel); }
  function $all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function wide() { return window.innerWidth >= 1000; }
  function analyticsSuppressed() {
    try {
      var params = new URLSearchParams(location.search);
      return params.has('funnel_audit') || params.get('internal') === 'true' || params.get('traffic_type') === 'internal' || params.has('deckaura_internal') || params.has('codex_audit') || params.has('alltools_audit');
    } catch (_) { return false; }
  }
  function track(name, data) {
    if (analyticsSuppressed()) return;
    try { if (typeof window.gtag === 'function') window.gtag('event', name, Object.assign({ page_funnel: 'tt2' }, data || {})); } catch (_) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (_) {}
  }
  function visitorId() {
    var key = 'deckaura_visitor_v1';
    try {
      var existing = localStorage.getItem(key);
      if (existing && /^[a-zA-Z0-9_-]{16,96}$/.test(existing)) return existing;
      var bytes = new Uint8Array(18); crypto.getRandomValues(bytes);
      var next = 'v_' + Array.prototype.map.call(bytes, function (x2) { return x2.toString(16).padStart(2, '0'); }).join('');
      localStorage.setItem(key, next);
      return next;
    } catch (_) { return 'v_' + Date.now().toString(16) + Math.random().toString(36).slice(2, 10); }
  }
  function reduced() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function scrollToId(id, offset) {
    var el = document.getElementById(id);
    if (!el) return;
    try { window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - (offset || 76), behavior: 'auto' }); } catch (_) {}
  }
  function live(msg) {
    s.live = msg;
    var el = $('[data-tt2-live]');
    if (el) el.textContent = msg;
  }
  function save() {
    try {
      localStorage.setItem(KEY, JSON.stringify({
        focus: s.focus, horizon: s.horizon, q: s.q, ctx: s.ctx, eventDef: s.eventDef,
        plan: s.planTouched ? s.plan : '', phase: s.phase === 'result' ? 'result' : 'form',
        drawVersion: 3, drawn: s.drawn, readingId: s.readingId, server: s.server, insightLimit: s.insightLimit
      }));
    } catch (_) {}
  }
  function fc() { for (var i = 0; i < FOCI.length; i += 1) if (FOCI[i].k === s.focus) return FOCI[i]; return null; }
  function hz() { for (var i = 0; i < HORIZONS.length; i += 1) if (HORIZONS[i].k === s.horizon) return HORIZONS[i]; return HORIZONS[1]; }
  function pkgOf(k) { for (var i = 0; i < PKGS.length; i += 1) if (PKGS[i].k === k) return PKGS[i]; return PKGS[0]; }
  function questionForReading() {
    return s.q.normalize('NFC').replace(/\s+/g, ' ').trim();
  }
  var artMapCache = null;
  function artMap() {
    if (artMapCache) return artMapCache;
    var node = document.getElementById('tarotCardArtMap');
    try { artMapCache = node ? JSON.parse(node.textContent || '{}') : {}; } catch (_) { artMapCache = {}; }
    return artMapCache;
  }
  function artIndexFor(name) {
    var major = MAJOR_ORDER.indexOf(name);
    if (major >= 0) return major;
    var match = /^(Ace|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|Page|Knight|Queen|King) of (Wands|Cups|Swords|Pentacles)$/.exec(name);
    if (!match) return -1;
    var offsets = { Cups: 22, Pentacles: 36, Swords: 50, Wands: 64 };
    return offsets[match[2]] + RANKS.indexOf(match[1]);
  }
  function cardArt(name) {
    return window.DeckauraTarotArt ? window.DeckauraTarotArt.url(name, 720) : '';
  }

  /* ------------ deterministic draw (design hash clone, 78 deck) ------------ */
  function hash(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i += 1) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  function drawFor(focus, horizon, q) {
    var seed = hash(focus + '|' + horizon + '|' + q.trim().toLowerCase()) >>> 0;
    function next() {
      seed = (seed + 0x6D2B79F5) >>> 0;
      var value = seed;
      value = Math.imul(value ^ value >>> 15, value | 1);
      value ^= value + Math.imul(value ^ value >>> 7, value | 61);
      return ((value ^ value >>> 14) >>> 0) / 4294967296;
    }
    var deck = DECK.map(function (_, index) { return index; });
    for (var i = deck.length - 1; i > 0; i -= 1) {
      var j = Math.floor(next() * (i + 1)), card = deck[i];
      deck[i] = deck[j]; deck[j] = card;
    }
    return deck.slice(0, 3).map(function (index) { return { i: index, o: next() < 0.5 ? 'rev' : 'up' }; });
  }
  function validDraw(draw) {
    return Array.isArray(draw) && draw.length === 3 && draw.every(function (card, index) {
      return card && Number.isInteger(card.i) && card.i >= 0 && card.i < DECK.length && /^(up|rev)$/.test(card.o) && draw.findIndex(function (other) { return other.i === card.i; }) === index;
    });
  }
  function drawnCards() {
    return (s.drawn || []).map(function (x, k) {
      var card = DECK[x.i];
      var face = card.d[x.o];
      var summaries = ['The pace of the situation now.', 'A symbolic timing cue inside your chosen horizon.', 'The condition that could change the pace.'];
      var meanings = ['Read this as a prompt to compare the current momentum with what is actually happening.', 'This position describes readiness and pace, not a guaranteed date.', 'Watch for a concrete change before treating the window as open.'];
      return { name: card.n, glyph: card.d.g, orientation: x.o === 'rev' ? 'Reversed' : 'Upright', o: x.o, p: face.p, sum: summaries[k], body: meanings[k], role: ROLES[k].role, no: ROLES[k].no, hint: '' };
    });
  }
  function metrics() {
    var cards = drawnCards();
    if (!cards.length) return { pace: 'Steady', signal: 'Conditional', pos: 'Middle of your horizon', pct: '50%', posWord: 'middle' };
    var score = cards.reduce(function (a, c) { return a + c.p; }, 0);
    var rev = cards.filter(function (c) { return c.o === 'rev'; }).length;
    var pace = score >= 5 ? 'Accelerating' : score >= 3 ? 'Steady' : score >= 2 ? 'Developing' : 'Delayed';
    var signal = rev === 0 ? 'Strong' : rev === 3 ? 'Unclear' : 'Conditional';
    var map = { Accelerating: ['Early in your horizon', '18%', 'first stretch'], Steady: ['Early to middle', '36%', 'first half'], Developing: ['Middle of your horizon', '52%', 'middle'], Delayed: ['Later in your horizon', '80%', 'later part'] };
    var m = map[pace];
    return { pace: pace, signal: signal, pos: m[0], pct: m[1], posWord: m[2], key: { Accelerating: 'early', Steady: 'early_middle', Developing: 'middle', Delayed: 'later' }[pace] };
  }
  function designLensNote() {
    var m = metrics();
    return t('The signal leans toward the {position} of your {horizon} horizon. Movement depends on the condition shown by your third card.', { position: t(m.posWord), horizon: t(hz().long) });
  }
  function lensTexts() {
    if (personalizedInsight()) return { note: s.server.lensNote + ' ' + (s.server.watchFor || ''), watchFor: s.server.watchFor || '' };
    return { note: designLensNote(), watchFor: '' };
  }
  function personalizedInsight() {
    return !!(s.server && s.server.key === serverKey() && s.server.windowKey === metrics().key && /^model_(initial|retry)$/.test(s.server.source || '') && typeof s.server.lensNote === 'string' && s.server.lensNote.trim());
  }
  function serverKey() { return s.focus + '|' + s.horizon + '|' + questionForReading() + '|' + s.ctx.trim() + '|' + pageLocale(); }

  /* ------------ server insight ------------ */
  function resetTimestamp(value) {
    if (value == null || value === '') return 0;
    var number = Number(value);
    var timestamp = Number.isFinite(number) && number > 0 ? (number < 1e12 ? number * 1000 : number) : Date.parse(String(value));
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0;
  }
  function insightRetryAt(data, retryAfter) {
    var next = resetTimestamp(data.nextAt || data.resetAt || (data.freeReadings && data.freeReadings.nextAt));
    if (next) return next;
    if (retryAfter) {
      var seconds = Number(retryAfter);
      return Number.isFinite(seconds) && seconds >= 0 ? Date.now() + seconds * 1000 : resetTimestamp(retryAfter);
    }
    return 0;
  }
  function clearInsightLimit() {
    clearTimeout(insightRetryTimer); insightRetryTimer = 0;
    s.insightLimit = null;
  }
  function insightRetryBlocked() {
    return !!(s.insightLimit && s.insightLimit.key === serverKey() && s.insightLimit.nextAt > Date.now());
  }
  function scheduleInsightRetry() {
    clearTimeout(insightRetryTimer); insightRetryTimer = 0;
    if (!insightRetryBlocked()) return;
    insightRetryTimer = setTimeout(function () {
      insightRetryTimer = 0;
      if (s.phase === 'result') renderLens();
    }, Math.min(2147483647, s.insightLimit.nextAt - Date.now() + 50));
  }
  function insightUnavailableText() {
    var limit = s.insightLimit;
    if (!limit || limit.key !== serverKey()) return t('Personalized summary is temporarily unavailable. This is a basic card-based reflection. Your question and cards are saved.');
    var ownLimit = /^(visitor|device)_rate_limit$/.test(limit.reason);
    var message = t(limit.kind === 'quota'
      ? ownLimit ? 'Your personalized free summary limit has been reached. Your question and cards are saved.' : 'Personalized summaries are temporarily limited. Your question and cards are saved.'
      : 'Personalized summaries are temporarily busy. Your question and cards are saved.');
    if (limit.nextAt > Date.now()) {
      var date;
      try { date = new Intl.DateTimeFormat(pageLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(limit.nextAt)); }
      catch (_) { date = new Date(limit.nextAt).toLocaleString(); }
      message += ' ' + t('You can retry after {time}.', { time: date });
    } else if (limit.nextAt && limit.kind !== 'quota') message += ' ' + t('You can retry now.');
    else if (!limit.nextAt) message += ' ' + t('The service did not provide a reset time. You can retry manually.');
    return message;
  }
  function fetchInsight(force) {
    if (s.fetching || s.phase !== 'result' || !s.drawn) return;
    var key = serverKey(), requestedReadingId = s.readingId;
    if (insightRetryBlocked() || (!force && s.insightLimit && s.insightLimit.key === key)) { renderLens(); scheduleInsightRetry(); return; }
    if (!force && s.server && s.server.key === key) return;
    s.fetching = true;
    s.insightFailed = false;
    s.insightLimited = false;
    clearInsightLimit();
    renderLens();
    var cards = drawnCards().map(function (c) { return { name: c.name, orientation: c.orientation, role: c.role }; });
    var body = {
      focus: s.focus, horizon: s.horizon,
      question: questionForReading(),
      context: s.ctx.trim().slice(0, 400),
      locale: pageLocale(),
      cards: cards, visitorId: visitorId(), readingId: s.readingId
    };
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    insightController = ac;
    var timer = ac ? setTimeout(function () { ac.abort(); }, 16000) : 0;
    return fetch(API_BASE + '/insight', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body), signal: ac ? ac.signal : undefined
    }).then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, data: j && typeof j === 'object' ? j : {}, retryAfter: r.headers && r.headers.get('Retry-After') }; }); })
      .then(function (res) {
        if (s.readingId !== requestedReadingId || s.phase !== 'result') return;
        if (res.ok && res.data && res.data.lensNote) {
          s.server = { key: key, lensNote: res.data.lensNote, watchFor: res.data.watchFor || '', source: res.data.servedSource || '', windowKey: res.data.timingWindow && res.data.timingWindow.key || '' };
          save();
          track('tt2_insight_served', { source: res.data.servedSource || '' });
        } else {
          s.insightFailed = true;
          s.insightLimited = res.status === 429 && res.data.error === 'free_limit_reached';
          if (res.status === 429) {
            s.insightLimit = { key: key, kind: s.insightLimited ? 'quota' : 'traffic', reason: String(res.data.reason || ''), nextAt: insightRetryAt(res.data, res.retryAfter) };
            save();
            scheduleInsightRetry();
          }
          track('tt2_insight_unavailable', { reason: (res.data && res.data.error) || 'error' });
        }
      })
      .catch(function () {
        if (s.readingId !== requestedReadingId || s.phase !== 'result') return;
        s.insightFailed = true;
        track('tt2_insight_unavailable', { reason: 'network' });
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
        if (s.readingId !== requestedReadingId) return;
        s.fetching = false;
        renderLens();
        enableRail();
      });
  }

  /* ------------ checkout rail ------------ */
  function recoveryHost() { return $('[data-tt2-checkout-recovery-host]'); }
  function clearCartReview() {
    cartReviewKey = '';
    var host = recoveryHost();
    if (host) { host.__deckauraRecoveryGeneration = (host.__deckauraRecoveryGeneration || 0) + 1; host.replaceChildren(); host.hidden = true; }
  }
  function selectionKey() { return JSON.stringify([s.readingId, currentPkg(), timingEvidence()]); }
  function invalidateCheckout() { checkoutRun += 1; checkoutOperation = null; clearCartReview(); }
  function checkoutCurrent(operation) { return !!(operation && operation === checkoutOperation && operation.run === checkoutRun && s.phase === 'result' && validDraw(s.drawn) && operation.key === selectionKey()); }
  function cartReviewPending() { return !!(cartReviewKey && s.phase === 'result' && cartReviewKey === selectionKey()); }
  function recoverableCartReason(reason) { return /^(reading_cart_conflict|cart_changed|cart_operation_busy|cart_result_uncertain|cart_read_failed|cart_add_failed|checkout_guard_unavailable|checkout_navigation_busy)$/.test(reason); }
  function dismissReturnHint() { try { if (window.DeckauraCheckout && typeof window.DeckauraCheckout.dismissRecovery === 'function') window.DeckauraCheckout.dismissRecovery(); } catch (_) {} }
  function cartReviewReady(host) {
    if (!host || host !== recoveryHost() || !host.isConnected || !cartReviewPending()) return;
    s.checkoutBusy = false;
    checkoutStatus('');
    dismissReturnHint();
    renderPkgs(); renderSticky();
    try { host.setAttribute('tabindex', '-1'); host.focus({ preventScroll: true }); host.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }); } catch (_) {}
  }
  function railSignals() {
    var cards = drawnCards();
    var m = metrics();
    var out = cards.map(function (c) { return c.role + ': ' + c.name + ' (' + c.orientation + ')'; }).join('; ');
    out += '; Focus: ' + (fc() ? fc().label : 'Other') + '; Horizon: ' + hz().apiLong + '; Pace: ' + m.pace + '; Signal strength: ' + m.signal;
    return out;
  }
  function railContext() {
    var base = 'Tarot Timing V3. ' + railSignals() + '. Window position: ' + metrics().pos + '.';
    function quoted(label, bodyText, cap) {
      var room = 2900 - base.length - label.length - 2;
      if (room < 40) return '';
      return label + '"' + bodyText.slice(0, Math.min(cap, room)) + '"';
    }
    var ev = s.eventDef.trim();
    if (ev) base += quoted(' Customer follow-up question about this reading: ', ev, 200) || '';
    var ctx = s.ctx.trim();
    if (ctx) base += quoted(' Customer context: ', ctx, 500) || '';
    var lens = lensTexts();
    if (lens.watchFor) base += quoted(' Free watch-for sign already shown: ', lens.watchFor, 280) || '';
    return base;
  }
  function detailsProperty() {
    var m = metrics();
    var h = hz();
    var parts = ['Focus: ' + (fc() ? fc().label : 'Other') + '.', 'Horizon: ' + h.apiLong + ' (plan name: ' + h.plan + ').'];
    var ev = s.eventDef.trim();
    if (ev) parts.push('Follow-up question: "' + ev.slice(0, 200) + '".');
    var ctx = s.ctx.trim();
    if (ctx) parts.push('Context: "' + ctx.slice(0, 300) + '".');
    parts.push('Free result: pace ' + m.pace + ', signal ' + m.signal + ', ' + m.pos.toLowerCase() + '.');
    if (currentPkg() === 'delay') parts.push('Focus deliverables promised: ' + (DELAY_BULLETS[s.focus] || DELAY_BULLETS.other).join('; ') + '.');
    var lens = lensTexts();
    if (lens.watchFor) parts.push('Watch for: "' + lens.watchFor.slice(0, 200) + '"');
    return parts.join(' ').slice(0, 1300);
  }
  function currentPkg() { return s.plan; }
  function timingEvidence() {
    return {
      version: 'tarot-timing-v3', locale: pageLocale(), focus: s.focus, horizon: s.horizon,
      question: questionForReading(), followUpQuestion: s.eventDef.normalize('NFC').replace(/\s+/g, ' ').trim().slice(0, 200),
      context: s.ctx.normalize('NFC').replace(/\s+/g, ' ').trim().slice(0, 400),
      cards: drawnCards().map(function (card) { return { name: card.name, orientation: card.orientation, role: card.role }; })
    };
  }
  function checkoutIntent(details) {
    var quote = details.quote;
    return { endpoint: 'https://reading.deckaura.com/api/readings/intent', body: {
      kind: 'shared_tool', funnelVersion: 'enterprise-shared-tools-2026-08-v1', offerVariant: 'enterprise_shared_v1', snapshotVersion: 'reading-snapshot-v2',
      page: PAGE, toolType: 'Tarot Timing', tier: details.tier, expectedVariantId: details.variantId,
      idempotencyKey: 'shared_tt3_' + (globalThis.crypto && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)),
      question: questionForReading(), readingId: s.readingId, snapshot: Object.assign({}, details.snapshot, { timing: timingEvidence() }),
      displayedQuote: quote ? { variantId: quote.variantId, sku: quote.sku, priceCents: quote.cents, currency: quote.currency, country: quote.country, source: quote.source, requestId: quote.requestId } : null
    } };
  }
  function pricingForPlan(api, plan) {
    var snapshot = api && typeof api.getPricingSnapshot === 'function' ? api.getPricingSnapshot() : null;
    var tier = RAIL_TIERS[plan];
    var quote = snapshot && Array.isArray(snapshot.tiers) ? snapshot.tiers.find(function (entry) { return entry.tier === tier; }) : null;
    var available = !!(tier && snapshot && /^(ready|fallback_ready)$/.test(snapshot.status) && snapshot.checkout_allowed === true && /^[A-Z]{3}$/.test(snapshot.currency) && quote && quote.available === true && /^\d{10,20}$/.test(String(quote.variant_id)) && Number.isInteger(quote.price_minor) && quote.price_minor > 0 && typeof quote.formatted_price === 'string' && quote.formatted_price);
    return { snapshot: snapshot, quote: quote, tier: tier, available: available };
  }
  function syncPricing() {
    var api = window.DeckauraDeepReading, pricing;
    try {
      pricing = pricingForPlan(api, currentPkg());
      s.pricing = pricing.snapshot;
      if (pricing.available && typeof api.getSelectedTier === 'function' && api.getSelectedTier() !== pricing.tier && typeof api.setTier === 'function') api.setTier(pricing.tier);
      s.priceReady = !!(pricing.available && typeof api.getSelectedTier === 'function' && api.getSelectedTier() === pricing.tier && typeof api.isPriceReady === 'function' && api.isPriceReady());
      s.priceErr = !s.priceReady && !!(s.pricing && (s.pricing.status === 'error' || (/^(ready|fallback_ready)$/.test(s.pricing.status) && !pricing.available)));
    } catch (_) { s.priceReady = false; }
    if (s.priceReady && pricePoll) { clearInterval(pricePoll); pricePoll = 0; }
    renderPkgs();
    return s.priceReady;
  }
  function packagePrice(plan) {
    var tier = RAIL_TIERS[plan], quotes = s.pricing && s.pricing.tiers;
    var quote = Array.isArray(quotes) && quotes.find(function (entry) { return entry.tier === tier; });
    return quote && quote.available && quote.formatted_price ? quote.formatted_price : '—';
  }
  function checkoutPublicProperties(pack) {
    var properties = {}, tabs = $('[data-tt2-tabbar]');
    function label(selector, fallback) { var node = $(selector); return node && node.textContent.trim() || t(fallback); }
    properties[tabs && tabs.getAttribute('aria-label') || t('Choose your reading')] = t(pack.name);
    var focusButton = $all('[data-tt2-focus]').find(function (button) { return button.getAttribute('data-tt2-focus') === s.focus; });
    var focusText = fc() ? t(fc().label) : t('Other');
    if (focusButton) { var focusCopy = focusButton.cloneNode(true), dot = focusCopy.querySelector('[data-tt2-focus-dot]'); if (dot) dot.remove(); focusText = focusCopy.textContent.trim() || focusText; }
    properties[label('#focus-label', 'Reading focus')] = focusText;
    properties[label('#horizon-label', 'Which timeframe should we explore?')] = t(hz().long);
    drawnCards().forEach(function (card, index) {
      var node = $all('[data-tt2-card]')[index], role = node && node.querySelector('.tt2-eyebrow');
      properties[role && role.textContent.trim() || t(card.role)] = t(card.name) + ' (' + t(card.orientation) + ')';
    });
    return properties;
  }
  function enableRail() {
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.enable !== 'function' || s.phase !== 'result' || !s.drawn || s.checkoutBusy) return;
    var pack = pkgOf(currentPkg());
    try {
      var options = {
        type: 'Tarot Timing', teaser: false, resultReady: true,
        presentationVariant: 'tarot-timing-v3',
        canonicalPage: PAGE, visitorId: visitorId(),
        question: questionForReading(), readingId: s.readingId,
        curiosityQuestion: timingEvidence().followUpQuestion || questionForReading(),
        focus: s.focus,
        fixedFocus: s.focus,
        context: railContext(),
        signals: railSignals(),
        cards: railSignals(),
        spread: 'Three-card timing spread: Current Momentum, Timing Signal, Pace Changer',
        scope: pack.name + ' for a ' + (fc() ? fc().label.toLowerCase() : 'timing') + ' question read inside a ' + hz().apiLong + ' horizon from the three-card timing spread.',
        confidence: 'Conditional timing window from three cards; a reflective guide, not a fixed-date prediction.',
        tool: PAGE,
        checkoutIntent: checkoutIntent,
        checkoutRecoveryHost: recoveryHost(),
        onCartReviewReady: cartReviewReady,
        onPricingStateChange: syncPricing,
        onPriceReady: syncPricing,
        checkoutGuard: function (choice) {
          return choice.tier === RAIL_TIERS[currentPkg()] && s.phase === 'result' && validDraw(s.drawn) && questionForReading().length >= 8;
        },
        checkoutProperties: Object.assign({
          '_Presentation Variant': 'tarot-timing-v3',
          '_Page Design': 'tarot-timing-v3',
          '_Reading Title': t(pack.name),
          '_Details': detailsProperty()
        }, checkoutPublicProperties(pack))
      };
      if (railReadingId === s.readingId && typeof api.update === 'function') api.update(options);
      else { railReadingId = s.readingId; api.enable(options); }
      if (typeof api.setPreviewAuthorization === 'function') api.setPreviewAuthorization({ previewToken: '', transportFallback: false });
      if (!s.checkoutBusy && typeof api.setCheckoutPending === 'function') api.setCheckoutPending(false);
      syncPricing();
    } catch (_) {}
  }
  function ensureRuntime(retry) {
    if (window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function') return Promise.resolve(true);
    if (runtimeLoad) return runtimeLoad;
    if (runtimeFailed && !retry) return Promise.resolve(false);
    runtimeFailed = false;
    var script = document.querySelector('script[src*="deep-reading-cta-runtime.js"]');
    if (!script) { runtimeFailed = true; return Promise.resolve(false); }
    if (retry) {
      var replacement = document.createElement('script'), url = new URL(script.src, location.href);
      Array.prototype.forEach.call(script.attributes, function (attribute) { if (attribute.name !== 'src') replacement.setAttribute(attribute.name, attribute.value); });
      url.searchParams.set('_deckaura_retry', String(Date.now()) + '-' + (++runtimeAttempt));
      replacement.src = url.href;
      script.remove();
      script = replacement;
    }
    var promise = new Promise(function (resolve) {
      var done = false, timer;
      function finish() {
        if (done) return;
        done = true;
        clearTimeout(timer);
        script.removeEventListener('load', finish);
        script.removeEventListener('error', finish);
        var ready = !!(window.DeckauraDeepReading && typeof window.DeckauraDeepReading.enable === 'function');
        runtimeFailed = !ready;
        resolve(ready);
      }
      script.addEventListener('load', finish);
      script.addEventListener('error', finish);
      timer = setTimeout(finish, 8000);
      if (retry) document.head.appendChild(script);
    });
    runtimeLoad = promise;
    promise.then(function () { if (runtimeLoad === promise) runtimeLoad = null; });
    return promise;
  }
  function pollPrice(retry) {
    clearInterval(pricePoll);
    priceWaitMs = 0;
    s.priceReady = false;
    s.priceErr = false;
    if (syncPricing()) return;
    var requestedReadingId = s.readingId;
    ensureRuntime(!!retry).then(function (ready) {
      if (s.phase !== 'result' || s.readingId !== requestedReadingId) return;
      if (!ready) {
        clearInterval(pricePoll); pricePoll = 0;
        s.priceReady = false; s.priceErr = true;
        renderPkgs();
        return;
      }
      enableRail();
      if (retry && typeof window.DeckauraDeepReading.retryPricing === 'function') window.DeckauraDeepReading.retryPricing();
      syncPricing();
    });
    pricePoll = setInterval(function () {
      priceWaitMs += 500;
      var ready = syncPricing();
      if (ready) {
        clearInterval(pricePoll);
        s.priceReady = true;
        s.priceErr = false;
        renderPkgs();
      } else if (priceWaitMs >= 20000) {
        clearInterval(pricePoll);
        s.priceErr = true;
        renderPkgs();
      }
    }, 500);
  }
  function checkoutStatus(msg, tone) {
    $all('[data-tt2-checkout-status]').forEach(function (el) {
      el.textContent = msg || '';
      el.style.display = msg ? 'block' : 'none';
      el.style.color = tone === 'warn' ? '#D8B46A' : '#E47772';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
    });
  }
  document.addEventListener('deckaura:funnel-event', function (event) {
    var detail = event.detail || {}, data = detail.data || {}, canonical = data.page || data.tool;
    if (canonical !== PAGE || (data.presentation_variant && data.presentation_variant !== 'tarot-timing-v3') || (data.reading_id && data.reading_id !== s.readingId) || s.phase !== 'result' || !validDraw(s.drawn)) return;
    if (detail.name === 'checkout_prepare_start') {
      if (cartReviewPending() && !s.checkoutBusy) checkoutOperation = { run: ++checkoutRun, key: selectionKey() };
      if (!checkoutCurrent(checkoutOperation)) return;
      s.checkoutBusy = true; checkoutStatus(''); renderPkgs(); renderSticky();
    } else if (detail.name === 'checkout_prepare_error' && checkoutCurrent(checkoutOperation)) {
      var reason = String(data.reason || data.status || '').toLowerCase();
      s.checkoutBusy = false;
      if (recoverableCartReason(reason)) { cartReviewKey = selectionKey(); checkoutStatus(''); }
      else { cartReviewKey = ''; checkoutStatus(t('Checkout could not open. Your question and cards are saved. Please try again.'), 'err'); }
      renderPkgs(); renderSticky();
    }
  });
  async function startCheckout(via) {
    if (s.checkoutBusy) return false;
    enableRail();
    if (!syncPricing()) { checkoutStatus(t('Please wait for the selected price to be verified, then try again.'), 'warn'); return false; }
    var api = window.DeckauraDeepReading;
    if (!api || typeof api.checkoutNowAsync !== 'function') {
      checkoutStatus(t('Checkout is unavailable right now. Your reading is saved; please try again.'), 'err');
      return false;
    }
    clearCartReview();
    var operation = { run: ++checkoutRun, key: selectionKey() };
    checkoutOperation = operation;
    s.checkoutBusy = true;
    checkoutStatus('');
    dismissReturnHint();
    renderPkgs();
    track('tt2_checkout_started', { tier: RAIL_TIERS[currentPkg()], via: via || 'bar' });
    try {
      var outcome = await api.checkoutNowAsync();
      if (!checkoutCurrent(operation)) return false;
      if (!outcome || outcome.ok !== true) {
        var reason = String(outcome && outcome.reason || '').toLowerCase();
        if (cartReviewPending() || recoverableCartReason(reason)) { cartReviewKey = selectionKey(); checkoutStatus(''); return false; }
        checkoutStatus(t('Checkout could not open. Your question and cards are saved. Please try again.'), 'err');
        live(t('Checkout could not open. Your question and cards are saved. Please try again.'));
        return false;
      }
      return true;
    } catch (_) {
      if (!checkoutCurrent(operation)) return false;
      if (!cartReviewPending()) checkoutStatus(t('Checkout could not open. Your question and cards are saved. Please try again.'), 'err');
      return false;
    } finally {
      if (checkoutCurrent(operation)) { s.checkoutBusy = false; syncPricing(); renderSticky(); }
    }
  }

  /* ------------ renders ------------ */
  function renderBuilder() {
    var restoreNotice = $('[data-tt2-restore-notice]');
    if (restoreNotice) {
      restoreNotice.hidden = !s.restoreNotice;
      restoreNotice.textContent = s.restoreNotice ? t('Please reveal the updated timing cards to continue. Your question and context are kept.') : '';
    }
    $all('[data-tt2-focus]').forEach(function (btn) {
      var on = btn.getAttribute('data-tt2-focus') === s.focus;
      btn.disabled = s.busy || s.checkoutBusy || s.phase !== 'form';
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
      btn.setAttribute('aria-invalid', s.errs.focus ? 'true' : 'false');
      btn.style.color = on ? '#F5F0E8' : '#E6E1EC';
      btn.style.borderColor = on ? '#E18A45' : '#2E2C33';
      btn.style.background = on ? 'rgba(225,138,69,0.13)' : '#101015';
      var dot = btn.querySelector('[data-tt2-focus-dot]');
      if (dot) {
        dot.style.borderColor = on ? '#E18A45' : '#4A4650';
        dot.style.background = on ? '#E18A45' : 'transparent';
        dot.textContent = on ? '✓' : '';
      }
    });
    $all('[data-tt2-horizon]').forEach(function (btn) {
      var on = btn.getAttribute('data-tt2-horizon') === s.horizon;
      btn.disabled = s.busy || s.checkoutBusy || s.phase !== 'form';
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
      btn.setAttribute('aria-invalid', s.errs.horizon ? 'true' : 'false');
      btn.style.borderColor = on ? '#E18A45' : '#34323A';
      btn.style.background = on ? '#E18A45' : '#17171D';
      btn.style.color = on ? '#16110C' : '#E6E1EC';
    });
    var ta = $('[data-tt2-q]');
    if (ta) {
      ta.placeholder = t('What are you waiting to see happen?');
      ta.disabled = s.busy || s.checkoutBusy || s.phase !== 'form';
      ta.required = true;
      ta.minLength = 8;
      ta.maxLength = 360;
      if (ta.value !== s.q) ta.value = s.q;
      ta.style.borderColor = s.errs.q ? '#E47772' : '#2E2C33';
      ta.setAttribute('aria-invalid', s.errs.q ? 'true' : 'false');
    }
    var qCount = $('[data-tt2-qcount]');
    if (qCount) qCount.textContent = String(s.q.length);
    var ctxWrap = $('[data-tt2-ctxwrap]');
    if (ctxWrap) ctxWrap.style.display = s.ctxOpen ? 'block' : 'none';
    var ctxSign = $('[data-tt2-ctxsign]');
    if (ctxSign) ctxSign.textContent = s.ctxOpen ? '–' : '+';
    var ctxLabel = $('[data-tt2-ctxlabel]');
    if (ctxLabel) ctxLabel.textContent = t(s.ctxOpen ? 'Optional context' : 'Add optional context');
    var contextInput = $('[data-tt2-ctx]');
    if (contextInput) { contextInput.disabled = s.busy || s.checkoutBusy || s.phase !== 'form'; if (contextInput.value !== s.ctx) contextInput.value = s.ctx; }
    var consent = $('[data-tt2-consent]');
    if (consent) {
      if (consent.checked !== s.consent) consent.checked = s.consent;
      consent.setAttribute('aria-invalid', s.errs.consent ? 'true' : 'false');
    }
    var keys = Object.keys(s.errs);
    var box = $('[data-tt2-errbox]');
    if (box) {
      box.style.display = keys.length ? 'block' : 'none';
      var title = $('[data-tt2-errtitle]');
      if (title) title.textContent = t('Please check your question');
      var list = $('[data-tt2-errlist]');
      if (list) {
        while (list.firstChild) list.removeChild(list.firstChild);
        keys.forEach(function (k) {
          var li = document.createElement('li');
          li.textContent = s.errs[k];
          list.appendChild(li);
        });
      }
    }
    ['focus', 'horizon', 'q', 'consent'].forEach(function (k) {
      var el = $('[data-tt2-err-' + k + ']');
      if (el) {
        el.style.display = s.errs[k] ? 'block' : 'none';
        el.textContent = s.errs[k] || '';
      }
    });
    var submit = $('[data-tt2-act="submit"]');
    if (submit) {
      submit.disabled = s.busy;
      submit.style.cursor = s.busy ? 'progress' : 'pointer';
      submit.style.background = s.busy ? '#8A5B33' : '#E18A45';
    }
    var sl = $('[data-tt2-submitlabel]');
    if (sl) sl.textContent = t(s.busy ? 'Reading the cards…' : 'Reveal My Timing Cards');
  }
  function renderPhase() {
    var drawBox = $('[data-tt2-draw]');
    if (drawBox) drawBox.style.display = s.phase === 'draw' ? 'block' : 'none';
    var result = $('[data-tt2-result]');
    if (result) result.style.display = s.phase === 'result' ? 'block' : 'none';
    var builder = document.getElementById('builder');
    if (builder) builder.hidden = s.phase !== 'form';
    renderSticky();
  }
  function renderResult() {
    if (s.phase !== 'result' || !s.drawn) { renderPhase(); return; }
    var m = metrics();
    var f = fc();
    var h = hz();
    var set = function (sel, value) { var el = $(sel); if (el) el.textContent = value; };
    set('[data-tt2-lede]', questionForReading());
    set('[data-tt2-kpi-pace]', t(m.pace));
    set('[data-tt2-kpi-signal]', t(m.signal));
    var sigEl = $('[data-tt2-kpi-signal]');
    if (sigEl) sigEl.style.color = m.signal === 'Strong' ? '#ACD6B6' : (m.signal === 'Unclear' ? '#E47772' : '#D8B46A');
    set('[data-tt2-kpi-signalnote]', t(m.signal === 'Strong' ? 'The three cards agree on direction.' : 'The outcome depends on a condition, not a date.'));
    set('[data-tt2-kpi-pos]', t(m.pos));
    set('[data-tt2-kpi-posnote]', t(h.long));
    set('[data-tt2-lensend]', t(h.end));
    var marker = $('[data-tt2-marker]');
    if (marker) marker.style.left = m.pct;
    renderLens();
    renderCards();
    set('[data-tt2-pkglede]', t('Your question and these three cards stay with your reading.'));
    renderPkgs();
    renderPhase();
  }
  function renderLens() {
    var lens = lensTexts();
    var loading = s.fetching || (!s.insightFailed && !(s.server && s.server.key === serverKey()));
    var fallback = !loading && !personalizedInsight();
    var el = $('[data-tt2-lensnote]');
    if (el) { el.textContent = loading ? '' : lens.note; el.hidden = loading; }
    var status = $('[data-tt2-insight-status]');
    if (status) {
      status.textContent = loading ? t('Preparing your personalized summary…') : fallback ? insightUnavailableText() : '';
      status.hidden = !loading && !fallback;
    }
    $all('[data-tt2-act="retryinsight"]').forEach(function (button) {
      button.hidden = !fallback;
      button.disabled = loading || s.checkoutBusy || insightRetryBlocked();
      button.textContent = t('Retry personalized summary');
    });
  }
  var cardMeaningCache;
  function displayedCardMeaning(card) {
    if (cardMeaningCache === undefined) {
      cardMeaningCache = null;
      try {
        var node = $('[data-tt2-card-meanings]'), payload = JSON.parse(node ? node.textContent : '{}');
        var locale = pageLocale().replace(/_/g, '-'), language = locale.split('-')[0];
        var expected = locale === 'pt-br' ? 'pt-br' : language === 'pt' ? 'pt-pt' : /^(en|tr|de|es)$/.test(language) ? language : 'en';
        if (payload && payload.locale === expected && payload.cards && typeof payload.cards === 'object' && !Array.isArray(payload.cards)) cardMeaningCache = payload.cards;
      } catch (_) {}
    }
    var meanings = cardMeaningCache && Object.prototype.hasOwnProperty.call(cardMeaningCache, card.name) ? cardMeaningCache[card.name] : null;
    var text = meanings && Object.prototype.hasOwnProperty.call(meanings, card.orientation) ? meanings[card.orientation] : '';
    return typeof text === 'string' && text.trim() && text.length <= 600 ? text.trim() : t(card.body);
  }
  function renderCards() {
    var cards = drawnCards();
    var isWide = wide();
    $all('[data-tt2-card]').forEach(function (node, k) {
      var c = cards[k];
      if (!c) return;
      node.style.display = (isWide || k === Math.min(s.mCard, 2)) ? 'block' : 'none';
      var set = function (sel, value) { var el = node.querySelector(sel); if (el) el.textContent = value; };
      set('[data-tt2-cname]', t(c.name));
      set('[data-tt2-csum]', t(c.sum));
      set('[data-tt2-cbodytext]', displayedCardMeaning(c));
      set('[data-tt2-chint]', t(c.hint));
      var image = node.querySelector('[data-tt2-cimg]');
      var imageUrl = cardArt(c.name);
      if (image) {
        image.style.display = imageUrl ? 'block' : 'none';
        if (imageUrl && image.getAttribute('src') !== imageUrl) image.setAttribute('src', imageUrl);
        image.setAttribute('alt', imageUrl ? t(c.name) + ' · ' + t(c.orientation) : '');
        image.style.transform = c.o === 'rev' ? 'rotate(180deg)' : 'none';
      }
      var glyph = node.querySelector('[data-tt2-cglyph]');
      if (glyph) {
        glyph.textContent = c.glyph;
        glyph.style.display = imageUrl ? 'none' : 'flex';
        glyph.style.transform = c.o === 'rev' ? 'rotate(180deg)' : 'none';
        glyph.style.color = c.o === 'rev' ? '#D8B46A' : '#E18A45';
        glyph.style.borderColor = c.o === 'rev' ? 'rgba(216,180,106,0.45)' : 'rgba(225,138,69,0.42)';
      }
      var chip = node.querySelector('[data-tt2-corient]');
      if (chip) {
        chip.textContent = t(c.orientation);
        chip.style.color = c.o === 'rev' ? '#D8B46A' : '#E18A45';
        chip.style.borderColor = c.o === 'rev' ? 'rgba(216,180,106,0.45)' : 'rgba(225,138,69,0.42)';
      }
      var open = !!s.open[k];
      var body = node.querySelector('[data-tt2-cbody]');
      if (body) body.style.display = open ? 'block' : 'none';
      var toggle = node.querySelector('[data-tt2-cardtoggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        var tl = toggle.querySelector('[data-tt2-ctogglelabel]');
        if (tl) tl.textContent = t(open ? 'Hide interpretation' : 'Read interpretation');
        var chev = toggle.querySelector('[data-tt2-cchev]');
        if (chev) chev.textContent = open ? '▲' : '▼';
      }
    });
    var controls = $('[data-tt2-cardnav]');
    if (controls) controls.style.display = isWide ? 'none' : 'flex';
    var dotsWrap = $('[data-tt2-dotswrap]');
    if (dotsWrap) dotsWrap.style.display = isWide ? 'none' : 'flex';
    var counter = $('[data-tt2-counter]');
    if (counter) counter.textContent = t('{count} of 3', { count: Math.min(s.mCard, 2) + 1 });
    $all('[data-tt2-dot]').forEach(function (dot, i) {
      var on = s.mCard === i;
      dot.setAttribute('aria-current', on ? 'true' : 'false');
      var bar = dot.querySelector('span');
      if (bar) {
        bar.style.width = on ? '26px' : '10px';
        bar.style.background = on ? '#E18A45' : '#3A3841';
      }
    });
  }
  function renderPkgs() {
    var isWide = wide();
    var sel = currentPkg();
    var priceLoading = !s.priceReady;
    $all('[data-tt2-tabbar]').forEach(function (bar) { bar.style.display = 'grid'; bar.style.gridTemplateColumns = 'repeat(2,minmax(0,1fr))'; });
    $all('[data-tt2-tab]').forEach(function (tab) {
      var k = tab.getAttribute('data-tt2-tab');
      if (!RAIL_TIERS[k]) { tab.hidden = true; tab.style.display = 'none'; return; }
      var on = k === sel;
      tab.disabled = s.checkoutBusy;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
      tab.style.background = on ? 'rgba(225,138,69,0.14)' : 'transparent';
      tab.style.borderColor = on ? '#E18A45' : '#2E2C33';
      tab.style.color = on ? '#F5F0E8' : '#E6E1EC';
      var price = tab.querySelector('[data-tt2-tabprice]');
      if (price) price.textContent = packagePrice(k);
    });
    $all('[data-tt2-pkg]').forEach(function (card) {
      var k = card.getAttribute('data-tt2-pkg');
      if (!RAIL_TIERS[k]) { card.hidden = true; card.style.display = 'none'; return; }
      var on = k === sel;
      var pop = k === 'delay';
      card.hidden = !on;
      card.style.display = on ? 'block' : 'none';
      card.classList.toggle('tt2-pk-sel', on);
      card.style.background = on ? '#1A1A21' : '#141419';
      card.style.borderColor = on ? '#E18A45' : (pop ? 'rgba(225,138,69,0.42)' : '#2E2C33');
      card.style.boxShadow = on ? '0 24px 60px rgba(0,0,0,0.4)' : 'none';
      card.style.marginTop = '0px';
      var price = card.querySelector('[data-tt2-price]');
      if (price) {
        price.textContent = packagePrice(k);
        price.style.color = '#F5F0E8';
      }
      var loadRow = card.querySelector('[data-tt2-priceload]');
      if (loadRow) loadRow.style.display = priceLoading ? 'flex' : 'none';
      var cta = card.querySelector('[data-tt2-pkgcta]');
      if (cta) {
        cta.hidden = true;
        cta.style.display = 'none';
        cta.textContent = t('Select this reading');
        cta.setAttribute('aria-pressed', on ? 'true' : 'false');
        cta.style.background = on ? '#E18A45' : 'transparent';
        cta.style.color = on ? '#16110C' : '#F5F0E8';
        cta.style.borderColor = on ? '#E18A45' : '#4A4650';
      }
    });
    var delayBullets = $('[data-tt2-delaybullets]');
    if (delayBullets) {
      var listItems = (DELAY_BULLETS[s.focus] || DELAY_BULLETS.other).slice(0, 3);
      while (delayBullets.firstChild) delayBullets.removeChild(delayBullets.firstChild);
      listItems.forEach(function (text) {
        var row = document.createElement('div');
        row.setAttribute('style', 'display:flex;gap:11px;align-items:flex-start;font-size:15px;line-height:1.45');
        var tick = document.createElement('span');
        tick.setAttribute('aria-hidden', 'true');
        tick.setAttribute('style', 'flex:none;margin-top:5px;width:11px;height:6px;border-left:1.6px solid #7FA88A;border-bottom:1.6px solid #7FA88A;transform:rotate(-45deg)');
        row.appendChild(tick);
        row.appendChild(document.createTextNode(t(text)));
        delayBullets.appendChild(row);
      });
    }
    var planBullet = $('[data-tt2-planbullet]');
    if (planBullet) planBullet.textContent = t('An action plan for your {horizon} horizon', { horizon: t(hz().long) });
    var evStatus = $('[data-tt2-evtstatus]');
    if (evStatus) {
      var okEv = s.eventDef.trim().length > 0;
      evStatus.textContent = t(okEv ? 'Saved with your reading' : 'Optional. Your original question is already included.');
      evStatus.style.color = okEv ? '#ACD6B6' : '#E6E1EC';
    }
    var pack = pkgOf(sel);
    $all('[data-tt2-eventdef]').forEach(function (input) { input.disabled = s.checkoutBusy; if (input.value !== s.eventDef) input.value = s.eventDef; });
    var set = function (selr, value) { var el = $(selr); if (el) el.textContent = value; };
    set('[data-tt2-selname]', t(pack.name));
    set('[data-tt2-selmeta]', packagePrice(sel) + ' · ' + t('One-time purchase'));
    set('[data-tt2-stickyname]', t(pack.name));
    set('[data-tt2-stickymeta]', packagePrice(sel) + ' · ' + t('One-time purchase'));
    $all('[data-tt2-act="checkout"]').forEach(function (checkout) {
      checkout.disabled = priceLoading || s.checkoutBusy;
      checkout.setAttribute('aria-busy', priceLoading || s.checkoutBusy ? 'true' : 'false');
      checkout.style.cursor = s.checkoutBusy ? 'progress' : priceLoading ? 'not-allowed' : 'pointer';
      checkout.style.background = priceLoading || s.checkoutBusy ? '#8A5B33' : '#E18A45';
    });
    $all('[data-tt2-checkoutlabel]').forEach(function (cl) {
      cl.textContent = s.checkoutBusy ? t('Opening secure checkout…') : priceLoading ? t('Verifying checkout…') : t('Get my reading · {price}', { price: packagePrice(sel) });
    });
    var perr = $('[data-tt2-priceerr]');
    if (perr) perr.style.display = s.priceErr ? 'flex' : 'none';
    var cmpWrap = $('[data-tt2-cmpwrap]');
    if (cmpWrap) cmpWrap.style.display = s.cmp ? 'block' : 'none';
    var cmpLabel = $('[data-tt2-cmplabel]');
    if (cmpLabel) cmpLabel.textContent = t(s.cmp ? 'Hide full comparison' : 'See full comparison');
    var cmpChev = $('[data-tt2-cmpchev]');
    if (cmpChev) cmpChev.textContent = s.cmp ? '▲' : '▼';
  }
  function renderSticky() {
    var bar = $('[data-tt2-sticky]');
    if (!bar) return;
    var holder = document.getElementById('checkoutDecision');
    var mainCheckout = holder && holder.querySelector('[data-tt2-act="checkout"]');
    if (mainCheckout && stickyObservedTarget !== mainCheckout) {
      if (stickyObserver) stickyObserver.disconnect();
      stickyObservedTarget = mainCheckout;
      stickyMainInView = false;
      if (typeof window.IntersectionObserver === 'function') {
        try {
          stickyObserver = new window.IntersectionObserver(function (entries) {
            entries.forEach(function (entry) { if (entry.target === stickyObservedTarget) stickyMainInView = entry.isIntersecting || entry.intersectionRatio > 0; });
            renderSticky();
          }, { threshold: 0 });
          stickyObserver.observe(mainCheckout);
        } catch (_) { stickyObserver = null; }
      }
    }
    if (!stickyObserver && !stickyScrollBound) {
      window.addEventListener('scroll', renderSticky, { passive: true });
      stickyScrollBound = true;
    }
    var rect = mainCheckout && mainCheckout.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    var mainVisible = !!(rect && rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth && rect.bottom > rect.top && rect.right > rect.left);
    var focused = document.activeElement;
    var editing = !!(focused && focused.closest && focused.closest('[data-tt2-eventdef]'));
    var viewport = window.visualViewport;
    var keyboardOpen = !!(viewport && viewportWidth < 1000 && viewport.scale <= 1.05 && viewport.height < viewportHeight * 0.78);
    var result = s.phase === 'result';
    var host = recoveryHost();
    var on = result && !cartReviewPending() && !(host && !host.hidden) && !mainVisible && !stickyMainInView && !editing && !keyboardOpen;
    bar.style.display = on ? 'flex' : 'none';
    bar.hidden = !on;
    document.body.classList.toggle('tt2-sticky-on', result);
  }
  function renderFaq() {
    $all('[data-tt2-faq]').forEach(function (btn) {
      var open = btn.getAttribute('aria-expanded') === 'true';
      var sign = btn.querySelector('[data-tt2-faqsign]');
      if (sign) sign.textContent = open ? '–' : '+';
      var body = btn.nextElementSibling;
      if (body) body.style.display = open ? 'block' : 'none';
    });
  }
  function render() {
    renderBuilder();
    renderResult();
    renderPhase();
    renderFaq();
  }

  /* ------------ flow ------------ */
  function validate() {
    var e = {};
    if (!fc()) e.focus = t('Choose a timing focus');
    if (!HORIZONS.some(function (horizon) { return horizon.k === s.horizon; })) e.horizon = t('Choose a realistic timing horizon');
    var question = questionForReading();
    if (question.length < 8 || question.length > 360) e.q = t('Write one clear question using 8 to 360 characters.');
    return e;
  }
  function firstInvalidControl(errors) {
    if (errors.focus) return $('[data-tt2-focus]');
    if (errors.horizon) return $('[data-tt2-horizon]');
    if (errors.q) return $('[data-tt2-q]');
    if (errors.consent) return $('[data-tt2-consent]');
    return $('[data-tt2-errbox]');
  }
  function submit() {
    if (s.busy || s.checkoutBusy) return;
    s.tried = true;
    var e = validate();
    s.errs = e;
    var n = Object.keys(e).length;
    if (n) {
      live(t('Please check your question'));
      renderBuilder();
      var first = firstInvalidControl(e);
      if (first) {
        try { first.scrollIntoView({ behavior: 'auto', block: 'center' }); } catch (_) {}
        window.setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (_) { try { first.focus(); } catch (__) {} } }, 0);
      }
      return;
    }
    invalidateCheckout();
    s.busy = true;
    s.restoreNotice = false;
    s.phase = 'draw';
    s.server = null;
    s.insightFailed = false;
    s.insightLimited = false;
    clearInsightLimit();
    s.open = {};
    s.mCard = 0;
    s.readingId = 'tt2-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    live(t('Drawing your three timing cards.'));
    track('tt2_submit', { focus: s.focus, horizon: s.horizon });
    render();
    scrollToId('builder', 76);
    clearTimeout(t1);
    var requestedReadingId = s.readingId, drawFocus = s.focus, drawHorizon = s.horizon, drawQuestion = questionForReading();
    t1 = setTimeout(function () {
      if (s.phase !== 'draw' || s.readingId !== requestedReadingId || !s.busy) return;
      t1 = 0;
      s.drawn = drawFor(drawFocus, drawHorizon, drawQuestion);
      s.phase = 'result';
      s.busy = false;
      save();
      var m = metrics();
      live(t('Your timing signal is ready.'));
      track('tt2_result', { pace: m.pace, signal: m.signal });
      render();
      setTimeout(function () { scrollToId('result', 72); }, 60);
      fetchInsight();
      enableRail();
      pollPrice();
    }, reduced() ? 0 : 1300);
  }
  function reset() {
    if (s.checkoutBusy) return;
    if (s.phase === 'result' && !window.confirm(t('Start a new question? Your current cards and result will be cleared.'))) return;
    clearTimeout(t1); t1 = 0;
    invalidateCheckout();
    checkoutStatus('');
    try { if (window.DeckauraDeepReading && typeof window.DeckauraDeepReading.disable === 'function') window.DeckauraDeepReading.disable(); } catch (_) {}
    if (insightController) insightController.abort();
    s.fetching = false;
    s.busy = false;
    clearInterval(pricePoll); pricePoll = 0;
    railReadingId = '';
    s.readingId = '';
    s.phase = 'form';
    s.restoreNotice = false;
    s.drawn = null;
    s.q = '';
    s.focus = 'other';
    s.horizon = '30d';
    s.consent = false;
    s.ctx = '';
    s.ctxOpen = false;
    s.eventDef = '';
    s.errs = {};
    s.tried = false;
    s.server = null;
    s.insightFailed = false;
    s.insightLimited = false;
    clearInsightLimit();
    s.plan = 'delay';
    s.planTouched = false;
    save();
    live(t('Form cleared. Build a new timing question.'));
    track('tt2_reset', {});
    render();
    scrollToId('builder', 76);
  }
  function shareResult() {
    if (s.phase !== 'result' || !s.drawn) return;
    var m = metrics();
    var cardNames = drawnCards().map(function (card) { return t(card.name) + ' (' + t(card.orientation) + ')'; }).join(', ');
    var text = t('My tarot timing signal: {pace}. Cards: {cards}.', { pace: t(m.pace), cards: cardNames });
    var payload = { title: t('My Tarot Timing Signal'), text: text, url: location.href.split('#')[0] };
    if (navigator.share) {
      navigator.share(payload).then(function () { live(t('Share sheet opened.')); track('tt2_share', { method: 'native' }); }).catch(function () {});
      return;
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text + ' ' + payload.url).then(function () { live(t('Result summary copied to your clipboard.')); track('tt2_share', { method: 'clipboard' }); }).catch(function () { live(t('Copy was not available in this browser.')); });
      return;
    }
    live(t('Sharing is not available in this browser.'));
  }
  function selectPlan(plan) {
    if (s.checkoutBusy || !Object.prototype.hasOwnProperty.call(RAIL_TIERS, plan)) return;
    if (s.plan !== plan) invalidateCheckout();
    s.plan = plan;
    s.planTouched = true;
    s.priceReady = false;
    checkoutStatus('');
    save();
    track('tt2_pkg', { pkg: plan });
    enableRail();
    pollPrice();
  }

  /* ------------ events ------------ */
  document.addEventListener('keydown', function (event) {
    if (s.checkoutBusy || !/^(ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Home|End)$/.test(event.key)) return;
    var element = event.target;
    if (!element || !element.closest) return;
    var selector = element.closest('[data-tt2-tab]') ? '[data-tt2-tab]' : element.closest('[data-tt2-horizon]') ? '[data-tt2-horizon]' : element.closest('[data-tt2-focus]') ? '[data-tt2-focus]' : '';
    if (!selector || ((selector === '[data-tt2-horizon]' || selector === '[data-tt2-focus]') && (s.busy || s.phase !== 'form'))) return;
    var items = $all(selector).filter(function (item) { return !item.hidden && !item.disabled; });
    var index = items.indexOf(element.closest(selector));
    if (index < 0 || !items.length) return;
    event.preventDefault();
    var next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (/^(ArrowLeft|ArrowUp)$/.test(event.key) ? -1 : 1) + items.length) % items.length;
    items[next].click();
    items[next].focus({ preventScroll: true });
  });
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    if (s.checkoutBusy && t.closest('[data-tt2-focus],[data-tt2-horizon],[data-tt2-tab],[data-tt2-pkg],[data-tt2-act="reset"]')) return;
    var focusBtn = t.closest('[data-tt2-focus]');
    if (focusBtn) {
      if (s.busy || s.phase !== 'form') return;
      s.focus = focusBtn.getAttribute('data-tt2-focus');
      save();
      if (s.tried) s.errs = validate();
      renderBuilder();
      return;
    }
    var hzBtn = t.closest('[data-tt2-horizon]');
    if (hzBtn) {
      if (s.busy || s.phase !== 'form') return;
      s.horizon = hzBtn.getAttribute('data-tt2-horizon');
      save();
      if (s.tried) s.errs = validate();
      renderBuilder();
      return;
    }
    var cardToggle = t.closest('[data-tt2-cardtoggle]');
    if (cardToggle) {
      var node = cardToggle.closest('[data-tt2-card]');
      var k = Number(node.getAttribute('data-tt2-card'));
      s.open[k] = !s.open[k];
      if (s.open[k]) track('tt2_card_open', { index: k });
      renderCards();
      return;
    }
    var dot = t.closest('[data-tt2-dot]');
    if (dot) { s.mCard = Number(dot.getAttribute('data-tt2-dot')); live(translate('{count} of 3', { count: s.mCard + 1 })); renderCards(); return; }
    var tab = t.closest('[data-tt2-tab]');
    if (tab) { selectPlan(tab.getAttribute('data-tt2-tab')); return; }
    var pkgCta = t.closest('[data-tt2-pkgcta]');
    if (pkgCta) {
      var pcard = pkgCta.closest('[data-tt2-pkg]');
      selectPlan(pcard.getAttribute('data-tt2-pkg'));
      return;
    }
    var pkgCard = t.closest('[data-tt2-pkg]');
    if (pkgCard) { selectPlan(pkgCard.getAttribute('data-tt2-pkg')); return; }
    var faqBtn = t.closest('[data-tt2-faq]');
    if (faqBtn) {
      var open = faqBtn.getAttribute('aria-expanded') === 'true';
      faqBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
      track('tt2_faq', {});
      renderFaq();
      return;
    }
    var act = t.closest('[data-tt2-act]');
    if (!act) return;
    var kind = act.getAttribute('data-tt2-act');
    if (kind === 'submit') submit();
    else if (kind === 'reset' || kind === 'restart') reset();
    else if (kind === 'share') shareResult();
    else if (kind === 'ctxtoggle') { s.ctxOpen = !s.ctxOpen; renderBuilder(); }
    else if (kind === 'prev') { s.mCard = (s.mCard + 2) % 3; renderCards(); }
    else if (kind === 'next') { s.mCard = (s.mCard + 1) % 3; renderCards(); }
    else if (kind === 'cmp') { s.cmp = !s.cmp; if (s.cmp) track('tt2_compare', {}); renderPkgs(); }
    else if (kind === 'retryprice') {
      enableRail();
      pollPrice(true);
    }
    else if (kind === 'retryinsight' && !s.checkoutBusy && !insightRetryBlocked()) { track('tt2_insight_retry', {}); fetchInsight(true); }
    else if (kind === 'checkout') startCheckout('bar');
    else if (kind === 'tobuilder') scrollToId('builder', 76);
    else if (kind === 'topackages') scrollToId('packages', 76);
    else if (kind === 'tomethod') scrollToId('method', 76);
  });
  document.addEventListener('input', function (ev) {
    var t = ev.target;
    if (!t || !t.hasAttribute || s.checkoutBusy) return;
    if (t.hasAttribute('data-tt2-q')) {
      if (s.busy || s.phase !== 'form') { t.value = s.q; return; }
      s.q = t.value.slice(0, 360);
      save();
      var counter = $('[data-tt2-qcount]');
      if (counter) counter.textContent = String(s.q.length);
      if (s.tried) { s.errs = validate(); renderBuilder(); }
      return;
    }
    if (t.hasAttribute('data-tt2-ctx')) { if (s.busy || s.phase !== 'form') { t.value = s.ctx; return; } s.ctx = t.value.slice(0, 400); save(); return; }
    if (t.hasAttribute('data-tt2-eventdef')) {
      if (s.phase !== 'result') { t.value = s.eventDef; return; }
      if (s.eventDef !== t.value.slice(0, 200)) invalidateCheckout();
      s.eventDef = t.value.slice(0, 200);
      save();
      if (s.eventDef.trim().length === 4) track('tt2_follow_up_question', {});
      enableRail();
      renderPkgs();
      return;
    }
  });
  document.addEventListener('change', function (ev) {
    var t = ev.target;
    if (t && t.hasAttribute && t.hasAttribute('data-tt2-consent')) {
      if (s.busy || s.checkoutBusy || s.phase !== 'form') { t.checked = s.consent; return; }
      s.consent = t.checked;
      if (s.tried) { s.errs = validate(); renderBuilder(); }
    }
  });
  var touchX = null;
  document.addEventListener('touchstart', function (ev) {
    if (!ev.target.closest || !ev.target.closest('[data-tt2-cards]')) return;
    touchX = ev.touches && ev.touches[0] ? ev.touches[0].clientX : null;
  }, { passive: true });
  document.addEventListener('touchend', function (ev) {
    if (touchX == null) return;
    if (!ev.target.closest || !ev.target.closest('[data-tt2-cards]')) { touchX = null; return; }
    var t = ev.changedTouches && ev.changedTouches[0];
    if (!t) { touchX = null; return; }
    var dx = t.clientX - touchX;
    touchX = null;
    if (Math.abs(dx) < 44 || wide()) return;
    s.mCard = (s.mCard + (dx < 0 ? 1 : 2)) % 3;
    live(translate('{count} of 3', { count: s.mCard + 1 }));
    renderCards();
  }, { passive: true });
  window.addEventListener('resize', function () {
    renderCards();
    renderPkgs();
    renderSticky();
  });
  document.addEventListener('focusin', renderSticky);
  document.addEventListener('focusout', function () { setTimeout(renderSticky, 0); });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', renderSticky);
    window.visualViewport.addEventListener('scroll', renderSticky);
  }
  window.addEventListener('pageshow', function () { invalidateCheckout(); s.checkoutBusy = false; checkoutStatus(''); if (s.phase === 'result') { renderLens(); scheduleInsightRetry(); enableRail(); pollPrice(); } });

  /* ------------ init ------------ */
  try {
    var saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (saved && typeof saved === 'object') {
      s.focus = FOCI.some(function (focus) { return focus.k === saved.focus; }) ? saved.focus : 'other';
      s.horizon = HORIZONS.some(function (horizon) { return horizon.k === saved.horizon; }) ? saved.horizon : '30d';
      s.q = String(saved.q || '').slice(0, 360);
      s.ctx = String(saved.ctx || '').slice(0, 400);
      s.eventDef = saved.drawVersion === 3 ? String(saved.eventDef || '').slice(0, 200) : '';
      if (saved.plan && pkgOf(saved.plan).k === saved.plan) { s.plan = saved.plan; s.planTouched = true; }
      if (saved.phase === 'result' && questionForReading().length >= 8) {
        s.restoreNotice = saved.drawVersion !== 3;
        s.drawn = saved.drawVersion === 3 && validDraw(saved.drawn) ? saved.drawn : null;
        if (validDraw(s.drawn)) {
          s.open = {};
          s.phase = 'result';
          s.readingId = /^[a-zA-Z0-9_-]{8,80}$/.test(saved.readingId || '') ? saved.readingId : 'tt3-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
          if (saved.server && saved.server.key === serverKey() && typeof saved.server.lensNote === 'string') s.server = saved.server;
          if (saved.insightLimit && saved.insightLimit.key === serverKey() && /^(quota|traffic)$/.test(saved.insightLimit.kind)) {
            s.insightLimit = { key: serverKey(), kind: saved.insightLimit.kind, reason: String(saved.insightLimit.reason || ''), nextAt: resetTimestamp(saved.insightLimit.nextAt) };
            s.insightFailed = true;
            s.insightLimited = s.insightLimit.kind === 'quota';
          }
          save();
        }
      }
    }
  } catch (_) {}
  render();
  if (s.phase === 'result') {
    fetchInsight();
    enableRail();
    pollPrice();
  }
})();
